import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

const API = `${process.env.REACT_APP_API_BASE_URL}/passport`;

export const getFxData = async (authCode) => {
  const { data } = await axios.get(`${API}/onboarding/fx/${authCode}`);

  return data;
};

export const useOnboardingPassport = (email, { enabled }) =>
  useQuery({
    queryKey: ['onboardingPassport'],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/onboarding/${email}`);

      return data;
    },
    enabled
  });

export const useOnboardingPassportDocuments = (authCode, { enabled }) =>
  useQuery({
    queryKey: ['useOnboardingPassportDocuments'],
    queryFn: async () => {
      try {
        const { data } = await axios.get(`${API}/onboarding/documents/${authCode}`);

        return data;
      } catch (e) {
        return e.response.data;
      }
    },
    enabled
  });

export const usePassportRequests = () =>
  useQuery({
    queryKey: ['passportRequests'],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/requests`);

      return data;
    }
  });

export const usePassportOffers = () =>
  useQuery({
    queryKey: ['passportOffers'],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/offers`);

      return data;
    }
  });

export const savePassportRequest = async (data) => {
  const resp = await axios.post(`${API}/request`, data);

  return resp.data;
};

export const updatePassportRequest = async (passportRequestId, data) => {
  const resp = await axios.patch(`${API}/request/${passportRequestId}`, data);

  return resp.data;
};

export const savePassportOnboardingsDocuments = async (authCode, data) => {
  const resp = await axios.post(`${API}/onboarding/documents/${authCode}`, data);

  return resp.data;
};

export const submitPassportOnboardingsDocuments = async (authCode) => {
  const resp = await axios.post(`${API}/onboarding/documents/${authCode}/submit`, {});

  return resp.data;
};

export const getPassportRequest = async (email) => {
  const { data } = await axios.get(`${API}/onboarding/${email}`);

  return data;
};

export const createPutPresignedUrl = async (fileExtension) => {
  const resp = await axios.get(`${API}/documents/put-presigned-url?fileExtension=${fileExtension}`);

  return resp.data;
};

export const createGetPresignedUrl = async (key) => {
  const resp = await axios.get(`${API}/documents/get-presigned-url?key=${key}`);

  return resp.data;
};

export const trackPassportRequestProgress = async (requestId, key, value = true) => {
  const resp = await axios.post(`${API}/request/${requestId}/track/progress`, {
    [key]: value
  });

  return resp.data;
};

export const createOnboardingPutPresignedUrl = (authCode) => async (fileExtension) => {
  const resp = await axios.get(
    `${API}/onboarding/documents/${authCode}/put-presigned-url?fileExtension=${fileExtension}`
  );

  return resp.data;
};

export const createOnboardingGetPresignedUrl = (authCode) => async (key) => {
  const resp = await axios.get(
    `${API}/onboarding/documents/${authCode}/get-presigned-url?key=${key}`
  );

  return resp.data;
};

export const requestPassportOffer = async (data) => await axios.post(`${API}/offers/request`, data);

export const updatePassportOnboardingRequest = async (email, data) =>
  await axios.patch(`${API}/onboarding/${email}`, data);

export const createPassportOnboardingStage1Request = async (data) =>
  await axios.post(`${API}/onboarding/stage-1`, data);

export const savePassportOnboardingStage2Request = async (data) =>
  await axios.post(`${API}/onboarding/stage-2`, data);

export const transferOnboardingRequestToAccount = async (data) =>
  await axios.post(`${API}/onboarding/account`, data);

// Hides the selected uploaded documents from the app. The server keeps the
// files; `data` is `{ request_id, document_ids: string[] }`.
export const removePassportDocuments = async (data) =>
  await axios.post(`${API}/onboarding/documents/remove`, data);

export const usePropertyRecommendations = (requestId, options = {}) =>
  useQuery({
    queryKey: ['propertyRecommendations', requestId],
    queryFn: async () => {
      try {
        return (await axios.get(`${API}/${requestId}/recommendations/properties`))?.data ?? null;
      } catch (error) {
        console.error('Error fetching property recommendations:', error);
      }
    },
    enabled: !!requestId && options.enabled !== false,
    retry: 1,
    ...options
  });
